/**
 * TitleFieldsPreview — read-only render of a title-page ``title_fields`` dict.
 *
 * A title-page module has no prose body, so the DRAFT tab and the Action History
 * preview have nothing to show from ``draft_content``. This component renders the
 * structured ``title_fields`` instead: the study title as a centered cover
 * heading, then every other field as a labeled row. It is the panel equivalent
 * of ``wordWriter.ts::updateTitlePageFields`` (which paints the same fields into
 * the live Word document).
 *
 * A field value may itself be markdown — including a whole markdown table (see
 * titleFieldsRecovery.ts) — so values render through react-markdown with the same
 * GFM + sub/sup plugin grammar the DRAFT tab uses for prose.
 */

import { makeStyles } from "@fluentui/react-components";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkSubSup } from "../../../services/remarkSubSup";
import { fonts, inkColors } from "../../tokens";
import { partitionTitleFields } from "../../services/titleFieldsView";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "10px" },
  cover: {
    fontFamily: fonts.body,
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "1.4",
    textAlign: "center",
    color: inkColors.ink,
    padding: "4px 0 8px",
  },
  row: { display: "flex", flexDirection: "column", gap: "1px" },
  label: {
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink3,
  },
  value: {
    fontFamily: fonts.body,
    fontSize: "13px",
    lineHeight: "1.45",
    color: inkColors.ink,
    // Markdown paragraphs inside a value shouldn't add vertical margins.
    "& p": { margin: 0 },
    "& table": { borderCollapse: "collapse", width: "100%" },
    "& th, & td": { border: `1px solid ${inkColors.ink5}`, padding: "2px 6px", textAlign: "left" },
  },
});

/** Decode literal escape sequences (``\n``/``\t``) that arrive unparsed in some
 *  JSON-stringified payloads. Conservative: only runs when a backslash exists. */
function decodeEscapedWhitespace(text: string): string {
  if (!text.includes("\\")) return text;
  return text
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\\\/g, "\\");
}

export interface TitleFieldsPreviewProps {
  fields: Record<string, string>;
  /** When set, render at most this many labeled rows (for compact previews such
   *  as the Action History timeline). The cover title always renders. */
  maxRows?: number;
}

const TitleFieldsPreview: React.FC<TitleFieldsPreviewProps> = ({ fields, maxRows }) => {
  const styles = useStyles();
  const { coverTitle, rows } = partitionTitleFields(fields);
  const shownRows = typeof maxRows === "number" ? rows.slice(0, maxRows) : rows;
  const hiddenCount = rows.length - shownRows.length;

  return (
    <div className={styles.root}>
      {coverTitle ? <div className={styles.cover}>{coverTitle}</div> : null}
      {shownRows.map((row) => (
        <div key={row.key} className={styles.row}>
          <span className={styles.label}>{row.label}</span>
          <div className={styles.value}>
            <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSubSup]}>
              {decodeEscapedWhitespace(row.value)}
            </ReactMarkdown>
          </div>
        </div>
      ))}
      {hiddenCount > 0 ? (
        <span className={styles.label}>
          +{hiddenCount} more field{hiddenCount === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
};

export default TitleFieldsPreview;
